/**
 * Turning a scenario file into a runnable config, and back again.
 *
 * The engine never looks at `scenario.id` to decide how to behave. A scenario is data all
 * the way down — sizes, buffers, thresholds, institutions, and the starting posture of the
 * regime — so a contributor can add a country by adding a file and nothing else.
 * `test/sim.test.ts` asserts this by loading every file in `data/scenarios` and running it
 * unmodified.
 *
 * The reverse direction matters just as much. Someone who has spent an afternoon replacing
 * our estimates with their own survey data should be able to get that back out as a
 * scenario file they can cite, version, and send to someone else. `configToScenario` is
 * that path: it is what turns an afternoon of slider-dragging into something publishable.
 */

import { DEFAULT_TUNABLES } from './constants.ts';
import type {
  MediaConfig,
  MovementConfig,
  PillarConfig,
  RegimeConfig,
  Scenario,
  SectorConfig,
  SimulationConfig,
  SourceRef,
  Tunables,
} from './types.ts';

export interface ScenarioOverrides {
  seed: number;
  horizonDays?: number;
  movement?: Partial<MovementConfig>;
  media?: Partial<MediaConfig>;
  regime?: Partial<RegimeConfig>;
  tunables?: Partial<Tunables>;
  /** Per-sector overrides keyed by sector id — what the setup screen's controls produce. */
  sectors?: Record<string, Partial<SectorConfig>>;
  /** Per-pillar overrides keyed by pillar id. */
  pillars?: Record<string, Partial<PillarConfig>>;
  /**
   * Sectors and pillars to add that the scenario does not define. Lets someone model a
   * sector we never thought of — a diaspora, a conscript army, a platform workforce —
   * without editing the scenario file or the engine.
   */
  addSectors?: SectorConfig[];
  addPillars?: PillarConfig[];
  /** Sector or pillar ids to drop entirely. */
  removeSectors?: string[];
  removePillars?: string[];
}

export function scenarioToConfig(
  scenario: Scenario,
  overrides: ScenarioOverrides,
): SimulationConfig {
  const removedSectors = new Set(overrides.removeSectors ?? []);
  const removedPillars = new Set(overrides.removePillars ?? []);

  const sectors = scenario.sectors
    .filter((s) => !removedSectors.has(s.id))
    .map((sector) => {
      const patch = overrides.sectors?.[sector.id];
      return patch ? { ...sector, ...patch } : { ...sector };
    })
    .concat((overrides.addSectors ?? []).filter((s) => !removedSectors.has(s.id)));

  const pillars = scenario.pillars
    .filter((p) => !removedPillars.has(p.id))
    .map((pillar) => {
      const patch = overrides.pillars?.[pillar.id];
      return patch ? { ...pillar, ...patch } : { ...pillar };
    })
    .concat((overrides.addPillars ?? []).filter((p) => !removedPillars.has(p.id)));

  const config: SimulationConfig = {
    scenarioId: scenario.id,
    demand: scenario.demand,
    seed: overrides.seed,
    horizonDays: overrides.horizonDays ?? scenario.horizonDays,
    sectors,
    pillars,
    movement: { ...scenario.movement, ...overrides.movement },
    media: { ...scenario.media, ...overrides.media },
    regime: { ...scenario.regime, ...overrides.regime },
  };
  return overrides.tunables ? { ...config, tunables: overrides.tunables } : config;
}

/**
 * Which tunables actually differ from the defaults.
 *
 * Used when serialising a customised run: carrying all sixteen defaults in a share link
 * would triple its length and tell the reader nothing about what the sender changed.
 */
export function changedTunables(tunables: Partial<Tunables> | undefined): Partial<Tunables> {
  if (!tunables) return {};
  const changed: Partial<Tunables> = {};
  for (const key of Object.keys(DEFAULT_TUNABLES) as (keyof Tunables)[]) {
    const value = tunables[key];
    if (typeof value === 'number' && value !== DEFAULT_TUNABLES[key]) changed[key] = value;
  }
  return changed;
}

export interface ExportOptions {
  /** Id for the exported scenario. Must be lowercase letters, digits and hyphens. */
  id: string;
  name: string;
  premise: string;
  /** Where these numbers came from. Required — an exported scenario has to stay accountable. */
  provenance: string;
  sources: SourceRef[];
  confidence: Scenario['confidence'];
}

/**
 * Turn a configuration — including one edited beyond recognition — back into a scenario
 * file that can be dropped into `data/scenarios/` and opened as a pull request.
 *
 * The provenance and sources are required arguments rather than optional ones. Someone
 * exporting a model they have re-parameterised with their own data has, by definition, a
 * source for it, and the one thing this project cannot afford is a corpus of scenario
 * files whose numbers nobody can account for.
 */
export function configToScenario(config: SimulationConfig, options: ExportOptions): Scenario {
  return {
    id: options.id,
    name: options.name,
    premise: options.premise,
    demand: config.demand,
    confidence: options.confidence,
    provenance: options.provenance,
    sources: options.sources.map((s) => ({ ...s })),
    horizonDays: config.horizonDays,
    sectors: config.sectors.map((s) => ({ ...s, threshold: { ...s.threshold } })),
    pillars: config.pillars.map((p) => ({ ...p, linkedSectors: [...p.linkedSectors] })),
    movement: { ...config.movement },
    media: { ...config.media },
    regime: { ...config.regime },
  };
}
